import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@silospot/i18n/react';
import { type VerifyEmailCodeInput, verifyEmailCodeSchema } from '@silospot/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  toast,
} from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthShell } from '../components/auth-shell';
import { ApiError, api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';

export function VerifyEmailPage(): ReactElement {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const form = useForm<VerifyEmailCodeInput>({
    resolver: zodResolver(verifyEmailCodeSchema),
    defaultValues: { code: '' },
  });

  const verify = useMutation({
    mutationFn: api.verifyEmailCode,
    onSuccess: ({ user: verified }) => {
      setUser(verified);
      navigate('/dashboard');
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : '';
      const key = message.includes('expired')
        ? 'verifyEmail.errors.expired'
        : message.includes('Too many')
          ? 'verifyEmail.errors.tooMany'
          : 'verifyEmail.errors.invalid';
      toast.error(t(key));
    },
  });

  const resend = useMutation({
    mutationFn: api.resendVerificationCode,
    onSuccess: () => toast.success(t('verifyEmail.resent')),
    onError: () => toast.error(t('errors.generic')),
  });

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.emailVerified) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>{t('verifyEmail.title')}</CardTitle>
          <CardDescription>{t('verifyEmail.description', { email: user.email })}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values) => verify.mutate(values))}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="123456"
                        className="text-center text-2xl tracking-[0.5em]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={verify.isPending}>
                {t('verifyEmail.submit')}
              </Button>
            </form>
          </Form>
          <div className="mt-4 text-center">
            <Button
              variant="link"
              size="sm"
              onClick={() => resend.mutate()}
              disabled={resend.isPending}
            >
              {t('verifyEmail.resend')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
